import cogoToast from 'components/ToastMessage/ToastMessage';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { map, filter, Observable, throttleTime } from 'rxjs';
import { useAuthActions } from 'modules/Auth/AuthContext';
import { AuthModalState } from 'modules/Auth/components/AuthModal';
import { cookieClientStorage } from 'utils/cookieStorage';
import { useCurrentUserId } from './hooks';

enum HANDLING_INTENTS {
  unverifiedEmail = 'UNVERIFIED_EMAIL',
  logoutWithoutRedirect = 'LOGOUT_NO_REDIRECT',
  logoutWithRedirect = 'LOGOUT_YES_REDIRECT',
  orgRequireAuth = 'ORG_REQUIRE_AUTH',
}

const ORG_AUTH_MISSING_IDP_CODE = 65000;

export interface RequestWatcherProps {
  stream: Observable<{
    status?: CF.API.Status;
  }>;
  throttleMs?: number;
}

export function RequestWatcher({ stream, throttleMs = 1500 }: RequestWatcherProps): JSX.Element | null {
  const { logout, openLoginModal } = useAuthActions();
  const currentUserId = useCurrentUserId();
  const router = useRouter();

  useEffect(() => {
    const isLoggedIn = Boolean(cookieClientStorage.get('session_token'));
    const { pathname, asPath } = router;
    const subs = stream
      .pipe(
        // map error stream to stream of intents
        map((errorObj = {}) => {
          const redirectInfo = errorObj?.status?.redirect_info;

          // ### Handle resource moved errors
          // currently, the resource-types which are supported are: 'app', 'user'
          // eventually, migrate the query-params to a Map for each resource-type.
          if (errorObj?.status?.code === 10050 && redirectInfo) {
            const { new_resource_id, resource_type } = redirectInfo;
            const resourceTypeToQueryKey: Record<CF.API.RedirectedResourceType, string> = {
              app: 'appId',
              model: 'modelId',
              workflow: 'workflowId',
              dataset: 'datasetId',
              user: 'userOrOrgId',
              module: 'moduleId',
            };
            const updatedQueryParam = { [resourceTypeToQueryKey[resource_type]]: new_resource_id };

            const updatedResourcePath = {
              pathname: router.pathname,
              query: { ...router.query, ...updatedQueryParam },
            };

            router.push(updatedResourcePath);
          }

          // apply any logic based on app-wide API errors, send to logging, etc

          // ### Handle invalid email error
          // we already check this on signup/login; but this additional redirect covers the case where the
          // unverified user is trying to access other pages.
          const details = errorObj.status?.details?.toLowerCase();
          if (errorObj.status?.code === 11008 && details?.includes('verify your primary email')) {
            return HANDLING_INTENTS.unverifiedEmail;

            // log the user out if authentication invalid
          } else if (isLoggedIn && errorObj.status && [11008, 11001, 11002].includes(errorObj.status.code) && pathname !== '/settings/security') {
            if (pathname !== '/login' && pathname !== '/signup') {
              return HANDLING_INTENTS.logoutWithRedirect;
            }
            return HANDLING_INTENTS.logoutWithoutRedirect;
          } else if (errorObj.status?.code === ORG_AUTH_MISSING_IDP_CODE && !pathname.includes('/settings/auth')) {
            return HANDLING_INTENTS.orgRequireAuth;
          }
          return false;
        }),
        // ignore errors we don't care about
        filter((x) => x !== false),
        // ignore concurrent failures to avoid multiple notifications/redirects
        throttleTime(throttleMs),
      )
      // perform side effects
      .subscribe((intent: HANDLING_INTENTS | false) => {
        switch (intent) {
          case HANDLING_INTENTS.unverifiedEmail: {
            // on app mount; when email has been verified, but, onboarding form isn't completed;
            // force to settings & display onboarding dialog
            openLoginModal(AuthModalState.SHOW_PENDING_VERIFICATION);
            break;
          }
          case HANDLING_INTENTS.logoutWithRedirect: {
            logout({ redirect: false });
            cogoToast.error('Login session expired; please log in again.');
            openLoginModal();
            break;
          }
          case HANDLING_INTENTS.logoutWithoutRedirect: {
            logout({ redirect: false });
            router.replace({ pathname: asPath });
            break;
          }
          case HANDLING_INTENTS.orgRequireAuth: {
            router.replace({
              pathname: `/${currentUserId}/settings/auth`,
              query: { recovery_path: encodeURIComponent(asPath) },
            });
            break;
          }
          default:
        }
      });

    return () => {
      subs.unsubscribe();
    };
  }, [stream, router]);

  return null;
}
